import { useEffect } from 'react';
import { apiFetch } from '../api';

// Active time, for the owner's overview of how much NordComply is used. One working session = from a first click, key press, scroll
// or mouse move until a pause of 10 minutes. In a session, the seconds up to 3 minutes after each movement count (someone fixing
// something in a Microsoft portal and coming back); a longer quiet does not count. The session is sent once, when it is over (and
// when the page is hidden or closed, and now and then in a long session), so the server is not called all the time. Everything is kept in
// memory only: nothing is stored on the person's device for it. The server adds only the difference for a session id.
const GRACE_MS = 3 * 60 * 1000;
const END_AFTER_MS = 10 * 60 * 1000;
const CHECK_EVERY_MS = 30 * 1000;
const SAFETY_SEND_MS = 15 * 60 * 1000;

const newId = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `s${Date.now()}${Math.random().toString(36).slice(2, 10)}`);

function post(s) {
  const seconds = Math.round(s.pending);
  if (seconds < 1) return Promise.resolve(true);
  const endedAt = Math.min(Date.now(), s.lastInteraction + GRACE_MS);
  return apiFetch('/api/activity-ping', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sid: s.sid, seconds, startedAt: new Date(Math.min(s.start, endedAt)).toISOString(), endedAt: new Date(endedAt).toISOString() }),
    keepalive: true,
  }).then((r) => r.ok).catch(() => false);
}

export default function useActivePing(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    let s = null; // { sid, start, lastInteraction, lastTick, pending, sentAt }
    const settle = () => {
      if (!s) return;
      const now = Date.now();
      const to = Math.min(now, s.lastInteraction + GRACE_MS);
      if (to > s.lastTick) s.pending += (to - s.lastTick) / 1000;
      s.lastTick = now;
    };
    const finish = () => {
      if (!s) return;
      settle();
      const done = s;
      s = null;
      post(done);
    };
    const touch = () => {
      const now = Date.now();
      if (s && now - s.lastInteraction > END_AFTER_MS) finish();
      if (!s) s = { sid: newId(), start: now, lastInteraction: now, lastTick: now, pending: 0, sentAt: now };
      else settle();
      s.lastInteraction = now;
    };
    const check = () => {
      if (!s) return;
      settle();
      if (Date.now() - s.lastInteraction > END_AFTER_MS) { finish(); return; }
      // A long session is also sent in between, so a crash never loses more than a little of it.
      if (Date.now() - s.sentAt > SAFETY_SEND_MS) { s.sentAt = Date.now(); post(s); }
    };
    let lastMove = 0;
    const onMove = () => { const now = Date.now(); if (now - lastMove > 1000) { lastMove = now; touch(); } };
    const events = ['mousedown', 'keydown', 'touchstart', 'wheel'];
    events.forEach((e) => window.addEventListener(e, touch, { passive: true, capture: true }));
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('scroll', onMove, { passive: true, capture: true });
    const timer = setInterval(check, CHECK_EVERY_MS);
    // The page is closed or put away: keep what is counted, and try to send it (the server only adds what is new).
    const onHide = () => {
      if (document.visibilityState !== 'hidden' || !s) return;
      settle();
      post(s);
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, touch, { capture: true }));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', onMove, { capture: true });
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
      if (s) { settle(); post(s); }
    };
  }, [enabled]);
}
